/**
 * Conversation API Helper
 * Handles all conversation-related API calls
 */

import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from './apiConfig';

/**
 * Get authorization headers with access token
 */
const getAuthHeaders = (accessToken) => ({
  'Authorization': `Bearer ${accessToken}`,
  'Content-Type': 'application/json'
});

/**
 * List all conversations for the current user
 */
export const listConversations = async (accessToken, includeArchived = false, limit = 50, offset = 0) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS.LIST}`, {
      params: { include_archived: includeArchived, limit, offset },
      headers: getAuthHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    console.error('Error listing conversations:', error);
    throw error;
  }
};

/**
 * Get a specific conversation by ID
 */
export const getConversation = async (accessToken, conversationId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS.GET(conversationId)}`,
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting conversation:', error);
    throw error;
  }
};

/**
 * Create a new conversation
 */
export const createConversation = async (accessToken, title, modelName) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS.CREATE}`,
      { title, model_name: modelName },
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

/**
 * Update conversation (e.g., change title)
 */
export const updateConversation = async (accessToken, conversationId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS.UPDATE(conversationId)}`,
      updates,
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating conversation:', error);
    throw error;
  }
};

/**
 * Delete a conversation
 */
export const deleteConversation = async (accessToken, conversationId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS.DELETE(conversationId)}`,
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};

/**
 * Archive a conversation
 */
export const archiveConversation = async (accessToken, conversationId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS.ARCHIVE(conversationId)}`,
      {},
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    console.error('Error archiving conversation:', error);
    throw error;
  }
};

/**
 * Get messages for a conversation
 */
export const getConversationMessages = async (accessToken, conversationId, limit = null, offset = 0) => {
  try {
    const params = { offset };
    if (limit) params.limit = limit;
    
    const response = await axios.get(
      `${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS.MESSAGES(conversationId)}`,
      { 
        params,
        headers: getAuthHeaders(accessToken)
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting conversation messages:', error);
    throw error;
  }
};

/**
 * Link existing documents to a conversation (from Document Manager)
 * Documents are already vectorized, so no re-processing needed
 */
export const linkDocumentsToConversation = async (accessToken, conversationId, documentIds) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/conversations/${conversationId}/link-documents`,
      documentIds,
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    console.error('Error linking documents to conversation:', error);
    throw error;
  }
};

/**
 * Submit feedback for an assistant response
 */
export const submitFeedback = async (accessToken, conversationId, messageId, rating, remarks = null) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/feedback`,
      {
        conversation_id: conversationId,
        message_id: messageId,
        rating: rating,
        remarks: remarks
      },
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }
};

/**
 * Get feedback for a specific message (check if feedback exists)
 */
export const getFeedbackForMessage = async (accessToken, messageId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/feedback/message/${messageId}`,
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return null; // No feedback exists
    }
    console.error('Error getting feedback for message:', error);
    throw error;
  }
};

/**
 * Get all feedbacks for a conversation
 */
export const getConversationFeedbacks = async (accessToken, conversationId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/feedback/conversation/${conversationId}`,
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting conversation feedbacks:', error);
    throw error;
  }
};

/**
 * Delete feedback (optional - allows user to remove their feedback)
 */
export const deleteFeedback = async (accessToken, feedbackId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/feedback/${feedbackId}`,
      { headers: getAuthHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting feedback:', error);
    throw error;
  }
};
